import { useEffect, useMemo, useState } from 'react';

import type { Store } from './useAisles.ts';

/**
 * Which of the household's shops you are standing in.
 *
 * <b>Per device, and deliberately not shared</b> — which is the one place this feature
 * departs from `groupByAisle` sitting on the list. Whether the list is sectioned at all is a
 * property of the list, and two people in the same shop must agree about it. Which building
 * you are in is a property of the person holding the phone: one of you can be in Tesco and
 * the other in Aldi, working the same list, and each should see it in the order of the shop
 * they are actually walking.
 *
 * It costs no request and no round trip, so it survives a dead spot — which matters, because
 * arriving at the shop is exactly when this gets changed.
 *
 * Remembered per group, because the shop you use for the flat is not the one you use for the
 * family house. Derived rather than synchronised, for the reason `useSelectedList` is: the
 * stored id is a *preference*, and what actually applies is whatever it resolves to against
 * the stores we have. An id for a shop somebody else has since deleted falls back to the
 * first and self-heals.
 *
 * The caller is expected to remount this per group (see the `key` on ListView).
 */
export function useSelectedStore(groupId: string, stores: Store[]) {
  const [preferredId, setPreferredId] = useState<string | null>(() => readSelectedStore(groupId));

  const selected = useMemo(
    () => stores.find((store) => store.id === preferredId) ?? stores[0] ?? null,
    [stores, preferredId],
  );

  // localStorage is an external system, so keeping it in step is what an effect is for.
  // Only the resolved value is persisted, so a stale id cannot linger.
  useEffect(() => {
    if (selected) {
      persistSelectedStore(groupId, selected.id);
    }
  }, [groupId, selected]);

  return { selected, select: setPreferredId };
}

function storageKey(groupId: string): string {
  return `bnb.selectedStoreId:${groupId}`;
}

/**
 * The stored preference, without subscribing to it.
 *
 * For the settings panel, which starts on the shop you are in — it is very often the one
 * you opened the panel to fix — but keeps its own selection from there, so arranging Aldi
 * does not move the list you have open at Tesco. Calling the hook twice would share that
 * selection through localStorage and do exactly that.
 */
export function readSelectedStoreId(groupId: string): string | null {
  return readSelectedStore(groupId);
}

function readSelectedStore(groupId: string): string | null {
  try {
    return localStorage.getItem(storageKey(groupId));
  } catch {
    return null;
  }
}

function persistSelectedStore(groupId: string, storeId: string): void {
  try {
    localStorage.setItem(storageKey(groupId), storeId);
  } catch {
    // Non-fatal: the choice just will not survive a reload.
  }
}
